/**
 * In-app events in the web app (docs/APP-EVENTS.md; lib/appEvents.ts): configures the one tracker from the session and the
 * person's choice, starts the click listener and the sends, and tells it the screen on every route change. Rendered once by
 * Layout (every signed-in screen, business or home). Off under Do Not Track or Global Privacy Control, when "Share usage data" is off,
 * and in a build that may not send (tests, the dev server against a real API).
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { parsePlacementTags, usageSharingOn } from "@logwave/core";
import { isHomeOrg } from "../api.ts";
import { localKV, makeTracker, optedOut, realSender, screenOf, setTracker, startAppEvents, tracker, usageChoice, type BuyTags } from "../lib/appEvents.ts";
import { useApi, useSessionMaybe } from "../session.tsx";
import { TAG } from "../lib/amazonTag.ts";

const env = ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {});
/** VITE_AMAZON_TAGS: "step=…-20,ask=…-20" (core parsePlacementTags); used only when VITE_AMAZON_TAG is set. */
export const BUY_TAGS: BuyTags = { ...(TAG ? { default: TAG } : {}), byPlacement: parsePlacementTags(env.VITE_AMAZON_TAGS) };
/** Settings fires this after the person changes "Share usage data", so the bridge reads the choice again. */
export const USAGE_CHANGED = "cc-usage-changed";
export const APP_VERSION = /^[0-9A-Za-z.+-]{1,20}$/.test(env.VITE_APP_VERSION ?? "") ? env.VITE_APP_VERSION! : "0.1.0";

let made = false;

export function AppEventsBridge({ sender = realSender() }: { /** Tests: true to let the bridge run (with a mock API). */ sender?: boolean }) {
  const s = useSessionMaybe();
  const api = useApi();
  const loc = useLocation();
  const [choice, setChoice] = useState<boolean | null>(() => usageChoice());
  useEffect(() => { const f = () => setChoice(usageChoice()); window.addEventListener(USAGE_CHANGED, f); return () => window.removeEventListener(USAGE_CHANGED, f); }, []);
  useMemo(() => { if (!made && sender) { made = true; setTracker(makeTracker(localKV())); } }, [sender]);
  // Business or home alike (owner, 2026-10-07: "turn on home tracking").
  const on = sender && usageSharingOn({ signedIn: !!s, choice, dnt: optedOut() });
  const orgId = s?.org.id, who = s?.me.user.email, home = !!s && isHomeOrg(s.org);
  useEffect(() => {
    void tracker.configure({
      enabled: on, ...(orgId ? { orgId } : {}), ...(who ? { who } : {}), platform: "web", version: APP_VERSION,
      send: (o, batch) => api.sendAppEvents(o, batch, { keepalive: document.visibilityState === "hidden", home }),
    });
    return on ? startAppEvents(tracker, BUY_TAGS) : undefined;
  }, [on, orgId, who, api, home]);
  useEffect(() => { tracker.setScreen(screenOf(loc.pathname) ?? ""); }, [loc.pathname]);
  // Leaving the signed-in screens (the shared tablet takes over, or sign-out): no screen, so nothing more is kept.
  useEffect(() => () => { tracker.setScreen(""); }, []);
  return null;
}
